import TWEEN from '@tweenjs/tween.js'
import { camelCase } from 'change-case'
import { nextFrame } from '../helper'
import { MODE, TYPES } from '../const'
import { SHAPES_LANDSCAPE } from '../shapesLandscape'
import { SHAPES_PORTRAIT } from '../shapesPortrait'
import Shape from './Shape'
import Tetrahedron from '../tetrahedron/Tetrahedron'
import Content from './Content'
import FullscreenVideo from './FullscreenVideo'
import Mask from './Mask'
import Video from './Video'
import Img from './Img'
import Slides from './Slides'
import { getElementsIntroLandscape, getElementsIntroPortrait } from './elementsIntro'
import { getElementsStartLandscape, getElementsStartPortrait } from './elementsStart'

const { Cubic } = TWEEN.Easing

const PORTRAIT_IMG_KEYFRAMES = {
	inForward: {
		duration: 1500,
		top: () => [-0.5, 0],
		easing: Cubic.Out
	},
	inBackward: {
		duration: 1250,
		top: () => [0.25, 0],
		easing: Cubic.Out
	},
	outForward: {
		duration: 750,
		delay: 500,
		top: () => [0, 0.25],
		easing: Cubic.In
	},
	outBackward: {
		duration: 750,
		delay: 500,
		top: () => [0, -0.25],
		easing: Cubic.In
	}
}

const LANDSCAPE_IMG_KEYFRAMES = {
	inForward: {
		duration: 1000,
		left: () => [1, 0],
		easing: Cubic.Out
	},
	inBackward: {
		duration: 1000,
		left: () => [-1, 0],
		easing: Cubic.Out
	},
	outForward: {
		duration: 1000,
		delay: 450,
		left: () => [0, -0.5],
		easing: Cubic.In
	},
	outBackward: {
		duration: 1000,
		delay: 450,
		left: () => [0, 0.5],
		easing: Cubic.In
	}
}

export default class Panel {
	public readonly uid: string
	public visible = false
	private readonly type = TYPES.SECTION
	private readonly elements: any[] = []
	private readonly mode: any
	private readonly node: HTMLDivElement
	private readonly isMenuInverted: boolean
	private readonly isLogoInverted: boolean
	private readonly content: Content
	private readonly fullscreenVideo: FullscreenVideo
	private readonly tetrahedron: Tetrahedron
	private onMenuHide: () => void
	private onMenuShow: () => void
	private isLoaded = false

	constructor(node, mode, browser) {
		this.onTetrahedronClick = this.onTetrahedronClick.bind(this)
		this.onVideoOpen = this.onVideoOpen.bind(this)
		this.onVideoClose = this.onVideoClose.bind(this)

		const config = JSON.parse(node.dataset.config)
		this.uid = node.dataset.uid
		this.mode = mode
		this.node = node

		this.isMenuInverted = config.burger_color === 'black'
		this.isLogoInverted = config.logo_color === 'black'

		const videoNode = this.node.querySelector(`.js-video`)
		const imgNodes = Array.from(this.node.querySelectorAll(`.js-image`))

		if (config.intro_panel) {
			this.elements = mode === MODE.LANDSCAPE ? getElementsIntroLandscape(browser) : getElementsIntroPortrait()
		} else if (config.start_panel) {
			this.elements = mode === MODE.LANDSCAPE ? getElementsStartLandscape() : getElementsStartPortrait()
		} else {
			const media = []
			if (videoNode) {
				media.push(
					new Video({
						id: this.uid,
						mode,
						node: videoNode,
						keyframes: mode === MODE.LANDSCAPE ? LANDSCAPE_IMG_KEYFRAMES : PORTRAIT_IMG_KEYFRAMES
					})
				)
			}

			if (imgNodes.length === 1) {
				media.push(
					new Img({
						id: this.uid,
						node: imgNodes[0],
						keyframes: mode === MODE.LANDSCAPE ? LANDSCAPE_IMG_KEYFRAMES : PORTRAIT_IMG_KEYFRAMES
					})
				)
			}

			if (imgNodes.length > 1) {
				media.push(
					new Slides({
						id: this.uid,
						nodes: imgNodes,
						keyframes: mode === MODE.LANDSCAPE ? LANDSCAPE_IMG_KEYFRAMES : PORTRAIT_IMG_KEYFRAMES
					})
				)
			}

			if (mode === MODE.LANDSCAPE) {
				this.elements = [
					new Mask(new Shape(SHAPES_LANDSCAPE.mask), media),
					...config.shapes.map((shape) => new Shape(SHAPES_LANDSCAPE[camelCase(shape)]))
				]
			} else {
				this.elements = [
					new Mask(new Shape(SHAPES_PORTRAIT.mask), media),
					new Shape(SHAPES_PORTRAIT[config.layout_mobile])
				]
			}
		}

		const contentNode = this.node.querySelector('.js-content')
		if (contentNode) {
			this.content = new Content(contentNode)
		}

		const videoFullNode = this.node.querySelector('.js-video-full')
		if (videoFullNode) {
			this.fullscreenVideo = new FullscreenVideo(videoFullNode, this.onVideoOpen, this.onVideoClose)
		}

		this.node.style.display = 'block'
		const tetrahedronEl = this.node.querySelector('.js-tetrahedron')
		if (tetrahedronEl) {
			this.tetrahedron = new Tetrahedron(tetrahedronEl, mode, this.onTetrahedronClick)
		}
		this.node.style.display = 'none'
	}

	init(gl, programs, onMenuShow, onMenuHide) {
		this.onMenuHide = onMenuHide
		this.onMenuShow = onMenuShow

		const { shapeProgram, imgProgram, slidesProgram, blurImgProgram } = programs
		this.elements.forEach((el) => {
			switch (el.type) {
				case TYPES.SHAPE:
					el.init(gl, shapeProgram)
					break
				case TYPES.IMG:
					el.init(gl, imgProgram)
					break
				case TYPES.VIDEO:
					el.init(gl, imgProgram)
					break
				case TYPES.BLUR_IMG:
					el.init(gl, imgProgram, blurImgProgram)
					break
				case TYPES.SLIDES:
					el.init(gl, slidesProgram)
					break
				case TYPES.MASK:
					el.init(gl, programs)
			}
		})
	}

	async animateIn(forward) {
		this.visible = true
		this.node.style.display = 'block'

		await Promise.all(this.elements.map((el) => el.animateIn(forward)))
		await nextFrame()

		if (this.tetrahedron) this.tetrahedron.show()
		if (this.fullscreenVideo) this.fullscreenVideo.show()
		if (this.content) this.content.show()
	}

	async animateOut(forward) {
		if (this.content) this.content.hide()
		if (this.fullscreenVideo) this.fullscreenVideo.hide()
		if (this.tetrahedron) this.tetrahedron.hide()

		await Promise.all(this.elements.map((el) => el.animateOut(forward)))

		this.node.style.display = 'none'
		this.visible = false
	}

	onVideoOpen() {
		if (this.content) this.content.hide()
		this.onMenuHide()
		const shapes = this.elements.filter((el) => el instanceof Shape)
		shapes.map((shape) => shape.animateOut(false))
	}

	async onVideoClose() {
		const shapes = this.elements.filter((el) => el instanceof Shape)
		await Promise.all(shapes.map((shape) => shape.animateIn(false)))
		if (this.content) this.content.show()
		this.onMenuShow()
	}

	onTetrahedronClick(id) {
		if (this.content) this.content.showItem(id)
	}

	async preload(gl, onLoad) {
		await Promise.all(this.elements.map((el) => el.preload(gl)))
		this.isLoaded = true
		onLoad()
	}

	resize(width, height, dpr) {
		this.node.style.display = 'block'

		if (this.content && this.mode === MODE.PORTRAIT) {
			const { width: w, height: h } = this.content.rect
			this.elements.forEach((element) =>
				element.setAnchor(1 - Math.round(w * dpr) / width, 1 - Math.round(h * dpr) / height)
			)
		}

		this.elements.forEach((el) => el.resize(width, height))
		if (this.tetrahedron) this.tetrahedron.resize(dpr)

		this.node.style.display = this.visible ? 'block' : 'none'
	}

	draw(gl) {
		if (!this.visible) return
		this.elements.forEach((el) => el.draw(gl))
		if (this.tetrahedron) this.tetrahedron.update()
	}
}
